import React from "react";

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Catches unhandled JS errors in the React tree and shows a
 * friendly fallback instead of a blank white screen.
 */
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("Uncaught error:", error, info);
    // Forward to PostHog if it has been initialised
    try {
      const ph = (window as any).posthog;
      if (ph?.captureException) ph.captureException(error);
    } catch {
      // analytics should never throw
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          fontFamily:
            "'Albert Sans', -apple-system, BlinkMacSystemFont, sans-serif",
          backgroundColor: "#FBFBF8",
          color: "#111817",
        }}
      >
        <div style={{ maxWidth: 420, textAlign: "center" }}>
          <h1
            style={{
              fontSize: "1.5rem",
              marginBottom: "0.75rem",
              fontWeight: 600,
            }}
          >
            Something went wrong
          </h1>
          <p
            style={{
              fontSize: "0.875rem",
              color: "#46504D",
              lineHeight: 1.6,
              marginBottom: "1.5rem",
            }}
          >
            An unexpected error occurred. Please reload the page. If the problem
            persists, contact us at{" "}
            <a
              href="mailto:noah-rodman@hotmail.com"
              style={{ color: "#00332F", textDecoration: "underline", textDecorationColor: "#2EE6D6" }}
            >
              noah-rodman@hotmail.com
            </a>
            .
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "0.625rem 1.5rem",
              borderRadius: "0.75rem",
              border: "none",
              backgroundColor: "#2EE6D6",
              color: "#00332F",
              fontSize: "0.875rem",
              cursor: "pointer",
            }}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
